import { styled } from '@mui/material';

import { MESSAGE_LIST_WIDE_QUERY } from '../MessageList/messageListContainer';
import { type MessageListItemProps, useMessageListItem } from './useMessageListItem';

type Props = MessageListItemProps;

type OwnerState = {
  unread: boolean;
};

const AVATAR_SIZE = 40;
const AVATAR_SIZE_WIDE = 32;
const SENDER_COLUMN_WIDTH = '12rem';
const PREVIEW_LINES = 2;

const Row = styled('button')<{ ownerState: OwnerState }>(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `${AVATAR_SIZE}px minmax(0, 1fr) auto`,
  gridTemplateAreas: `
    "avatar sender date"
    "avatar main main"
    "avatar tags tags"`,
  columnGap: theme.spacing(1.5),
  rowGap: theme.spacing(0.5),
  alignItems: 'baseline',
  width: '100%',
  margin: 0,
  border: 0,
  paddingBlock: theme.spacing(1.5),
  paddingInline: theme.spacing(2),
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
  font: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.shortest,
  }),
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '&[aria-current="true"]': {
    backgroundColor: theme.palette.action.selected,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
  [MESSAGE_LIST_WIDE_QUERY]: {
    gridTemplateColumns: `${AVATAR_SIZE_WIDE}px ${SENDER_COLUMN_WIDTH} minmax(0, 1fr) auto auto`,
    gridTemplateAreas: '"avatar sender main tags date"',
    columnGap: theme.spacing(2),
    alignItems: 'center',
    paddingBlock: theme.spacing(1),
    minHeight: theme.spacing(7),
  },
}));

const Avatar = styled('span')(({ theme }) => ({
  ...theme.typography.body2Emphasis,
  gridArea: 'avatar',
  alignSelf: 'start',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: AVATAR_SIZE,
  height: AVATAR_SIZE,
  borderRadius: '50%',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.elevation1,
  color: theme.palette.text.secondary,
  [MESSAGE_LIST_WIDE_QUERY]: {
    alignSelf: 'center',
    width: AVATAR_SIZE_WIDE,
    height: AVATAR_SIZE_WIDE,
    fontSize: theme.typography.caption.fontSize,
  },
}));

const Sender = styled('span')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  ...(ownerState.unread ? theme.typography.body1Emphasis : theme.typography.body1),
  gridArea: 'sender',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: ownerState.unread ? theme.palette.text.primary : theme.palette.text.secondary,
  [MESSAGE_LIST_WIDE_QUERY]: {
    ...(ownerState.unread ? theme.typography.body2Emphasis : theme.typography.body2),
  },
}));

const DateText = styled('time')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  ...(ownerState.unread ? theme.typography.body2Emphasis : theme.typography.body2),
  gridArea: 'date',
  justifySelf: 'end',
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
  color: ownerState.unread ? theme.palette.text.primary : theme.palette.text.secondary,
  [MESSAGE_LIST_WIDE_QUERY]: {
    minWidth: theme.spacing(8),
    textAlign: 'right',
  },
}));

const Main = styled('span')(({ theme }) => ({
  gridArea: 'main',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
  minWidth: 0,
  [MESSAGE_LIST_WIDE_QUERY]: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: theme.spacing(1),
  },
}));

const Subject = styled('span')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  ...(ownerState.unread ? theme.typography.body2Emphasis : theme.typography.body2),
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  [MESSAGE_LIST_WIDE_QUERY]: {
    flexShrink: 0,
    maxWidth: '50%',
  },
}));

const Preview = styled('span')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: PREVIEW_LINES,
  WebkitBoxOrient: 'vertical',
  overflowWrap: 'anywhere',
  [MESSAGE_LIST_WIDE_QUERY]: {
    display: 'block',
    flex: 1,
    minWidth: 0,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflowWrap: 'normal',
  },
}));

const Tags = styled('span')(({ theme }) => ({
  gridArea: 'tags',
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(0.5),
  [MESSAGE_LIST_WIDE_QUERY]: {
    flexWrap: 'nowrap',
    marginTop: 0,
  },
}));

/**
 * One received message in a `MessageList`: sender, subject, preview, date and content tags.
 * The whole row is a single button opening the message; it never nests other controls.
 */
export function MessageListItem(props: Props) {
  const hook = useMessageListItem(props);
  const ownerState = { unread: Boolean(props.unread) };

  return (
    <li data-testid={props['data-testid'] ?? 'message-list-item'}>
      <Row
        type="button"
        ownerState={ownerState}
        onClick={props.onClick}
        aria-current={props.selected ? 'true' : undefined}
      >
        <Avatar aria-hidden="true">{hook.initials}</Avatar>
        <Sender ownerState={ownerState}>{props.sender}</Sender>
        <DateText ownerState={ownerState} dateTime={props.dateTime}>{props.date}</DateText>
        <Main>
          <Subject ownerState={ownerState}>{props.subject}</Subject>
          {props.preview ? <Preview>{props.preview}</Preview> : null}
        </Main>
        {hook.hasTags ? <Tags>{props.tags}</Tags> : null}
      </Row>
    </li>
  );
}

export type { Props as MessageListItemProps };
